import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import integrationService, {
  IntegrationLog,
  getLogStatusColor,
  formatRelativeTime,
} from '../../services/integrationService';

interface IntegrationLogsProps {
  integrationId: string;
}

const IntegrationLogs: React.FC<IntegrationLogsProps> = ({ integrationId }) => {
  const [logs, setLogs] = useState<IntegrationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 20;

  useEffect(() => {
    loadLogs();
  }, [integrationId, page]);

  const loadLogs = async () => {
    try {
      const data = await integrationService.getIntegrationLogs(integrationId, limit, page * limit);
      if (page === 0) {
        setLogs(data.logs);
      } else {
        setLogs([...logs, ...data.logs]);
      }
      setHasMore(data.logs.length === limit);
    } catch (error) {
      console.error('Failed to load logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    setPage(page + 1);
  };

  const toggleExpand = (logId: string) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'error':
        return <XCircle size={16} className="text-red-600" />;
      case 'pending':
        return <Clock size={16} className="text-yellow-600" />;
      default:
        return <AlertCircle size={16} className="text-gray-600" />;
    }
  };

  if (loading && page === 0) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock size={48} className="mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Logs Yet</h3>
        <p className="text-gray-600">
          Integration logs will appear here when actions are triggered
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {logs.map((log) => (
        <div key={log.id} className="bg-white border rounded-lg overflow-hidden">
          {/* Log Header */}
          <button
            onClick={() => toggleExpand(log.id)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition"
          >
            <div className="flex items-center gap-3 flex-1">
              {getStatusIcon(log.status)}
              <div className="text-left flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900 capitalize">
                    {log.action.replace(/_/g, ' ')}
                  </span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${getLogStatusColor(log.status)}`}>
                    {log.status}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  {formatRelativeTime(log.createdAt)} • {log.duration}ms
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {expandedLog === log.id ? (
                <ChevronDown size={20} className="text-gray-400" />
              ) : (
                <ChevronRight size={20} className="text-gray-400" />
              )}
            </div>
          </button>

          {/* Expanded Details */}
          {expandedLog === log.id && (
            <div className="px-4 py-3 border-t bg-gray-50 space-y-3">
              {/* Error Message */}
              {log.errorMessage && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <div className="flex items-start gap-2">
                    <XCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-red-800 mb-1">Error</div>
                      <div className="text-sm text-red-700">{log.errorMessage}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Request Data */}
              {log.requestData && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Request Data</div>
                  <pre className="bg-white border rounded p-3 text-xs overflow-x-auto">
                    {JSON.stringify(log.requestData, null, 2)}
                  </pre>
                </div>
              )}

              {/* Response Data */}
              {log.responseData && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Response Data</div>
                  <pre className="bg-white border rounded p-3 text-xs overflow-x-auto">
                    {JSON.stringify(log.responseData, null, 2)}
                  </pre>
                </div>
              )}

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                <div>
                  <div className="text-xs text-gray-600">Log ID</div>
                  <code className="text-xs font-mono text-gray-900">{log.id}</code>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Duration</div>
                  <div className="text-xs font-mono text-gray-900">{log.duration}ms</div>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Load More */}
      {hasMore && (
        <div className="flex justify-center pt-4">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};

export default IntegrationLogs;
